import React, { useState } from 'react';
import { X, Mail, Send, CheckCircle, ArrowLeft } from 'lucide-react';
import authService from '../services/authService';

function ForgotPasswordModal({ isOpen, onClose, subdomain }) {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const resetForm = () => {
        setEmail('');
        setError('');
        setLoading(false);
        setSent(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await authService.forgotPassword(email, subdomain);
            setSent(true);
        } catch (err) {
            setError(err?.error || err?.message || 'Failed to send reset email. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm">
            <div className="glass-card rounded-2xl w-full max-w-md p-8 relative" style={{ background: 'rgba(14,14,26,0.95)' }}>
                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 transition-colors"
                    style={{ color: 'rgba(255,255,255,0.3)' }}
                    onMouseOver={(e) => e.target.style.color = '#cbb9ff'}
                    onMouseOut={(e) => e.target.style.color = 'rgba(255,255,255,0.3)'}
                >
                    <X className="h-5 w-5" />
                </button>

                {/* Header */}
                <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 mx-auto"
                        style={{ background: sent ? 'rgba(74,222,128,0.15)' : 'rgba(155,125,240,0.15)' }}>
                        {sent ? (
                            <CheckCircle className="h-8 w-8" style={{ color: '#4ade80' }} />
                        ) : (
                            <Mail className="h-8 w-8" style={{ color: '#cbb9ff' }} />
                        )}
                    </div>
                    <h2 className="text-xl font-bold text-white">
                        {sent ? 'Check Your Email' : 'Forgot Password'}
                    </h2>
                    <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {sent
                            ? 'We\'ve sent a password reset link to your email address'
                            : 'Enter your email address to receive a password reset link'}
                    </p>
                </div>

                {/* Error message */}
                {error && (
                    <div className="mb-4 p-3 rounded-lg text-sm"
                        style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.2)' }}>
                        {error}
                    </div>
                )}

                {/* Email Input Form */}
                {!sent && (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: 'rgba(155,125,240,0.5)' }} />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input pl-10"
                                    placeholder="you@example.com"
                                    required
                                />
                            </div>
                            <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
                                Enter the email address registered with your account
                            </p>
                        </div>
                        <button
                            type="submit"
                            disabled={loading || !email}
                            className="btn-primary w-full"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Sending...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    <Send className="h-4 w-4" />
                                    Send Reset Link
                                </span>
                            )}
                        </button>
                    </form>
                )}

                {/* Success state */}
                {sent && (
                    <div className="text-center space-y-4">
                        <div className="p-4 rounded-lg text-sm text-left"
                            style={{ background: 'rgba(74,222,128,0.1)', color: '#86efac', border: '1px solid rgba(74,222,128,0.2)' }}>
                            <p className="mb-2">
                                <strong>📧 Email sent!</strong>
                            </p>
                            <p>
                                If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly. Please check your inbox and follow the instructions.
                            </p>
                            <p className="mt-2 text-xs" style={{ color: 'rgba(134,239,172,0.7)' }}>
                                The link expires in 1 hour. If you don't see the email, check your spam folder.
                            </p>
                        </div>
                        <div className="flex space-x-3">
                            <button
                                type="button"
                                onClick={() => setSent(false)}
                                className="btn-secondary flex-1 text-sm"
                            >
                                <ArrowLeft className="h-4 w-4 inline mr-1" />
                                Change Email
                            </button>
                            <button
                                onClick={handleClose}
                                className="btn-primary flex-1 text-sm"
                            >
                                Back to Login
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ForgotPasswordModal;